import { useMemo, useState, useEffect, useRef } from 'react';
import { Typography, Spin, Empty, Tag } from 'antd';
import { ArrowUpOutlined } from '@ant-design/icons';
import { WiDaySunny, WiCloud, WiRain, WiRaindrop } from 'weather-icons-react';
import { useEventSelection } from '../../../contexts/useEventSelection';
import { useTheme } from '../../../contexts/useTheme';
import { useMinuteClock } from '../../../hooks/useMinuteClock';
import { usePermissions } from '../../../hooks/usePermissions';
import { useRainForecastData } from '../../../hooks/useRainForecastData';
import { useCollectionFreshness } from '../../../hooks/useCollectionFreshness';
import { isEventActive as checkEventActive } from '../../../utils/eventSource';
import { resolveActiveRainForecast } from '../../../utils/rainForecastSelection';
import { usePlayerOptional } from '../usePlayerContext';
import type { WidgetProps } from './types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;

type RainStatus = 0 | 1 | 2 | 3;

const RAIN_COLORS = {
  green: '#2dd598',
  orange: '#ff8e01',
  red: '#ff3b3c',
  yellow: '#e4e14d',
};

const getRainStatus = (rainType: number | null, fewDrops: boolean | null): RainStatus => {
  if (rainType === 0 && fewDrops) return 3;
  if (rainType === 2) return 2;
  if (rainType === 1) return 1;
  return 0;
};

const getStatusColor = (status: RainStatus): string => {
  switch (status) {
    case 0: return RAIN_COLORS.green;
    case 1: return RAIN_COLORS.orange;
    case 2: return RAIN_COLORS.red;
    case 3: return RAIN_COLORS.yellow;
    default: return RAIN_COLORS.green;
  }
};

const getStatusLabel = (status: RainStatus): string => {
  switch (status) {
    case 0: return 'No rain';
    case 1: return 'Rain expected';
    case 2: return 'Raining';
    case 3: return 'Drops at times';
    default: return 'Unknown';
  }
};

const getStatusIcon = (status: RainStatus, color: string, size: number) => {
  switch (status) {
    case 0: return <WiDaySunny size={size} color={color} />;
    case 1: return <WiCloud size={size} color={color} />;
    case 2: return <WiRain size={size} color={color} />;
    case 3: return <WiRaindrop size={size} color={color} />;
    default: return <WiDaySunny size={size} color={color} />;
  }
};

const getCardBackground = (status: RainStatus, isDark: boolean): string => {
  const color = getStatusColor(status);
  const opacity = isDark ? '0.25' : '0.15';
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const RainForecastWidget = ({ sizeClass, playerTimestamp }: WidgetProps) => {
  const { selectedEvent } = useEventSelection();
  const { theme } = useTheme();
  const player = usePlayerOptional();
  const isDark = theme === 'dark';
  const now = useMinuteClock();
  const { isRoot } = usePermissions();
  const selectedDateRange = player?.selectedDateRange ?? null;

  const isEventActive = checkEventActive(selectedEvent);
  const metadataTimestamps = useCollectionFreshness(['forecast_rain'], {
    interval: 30_000,
    enabled: isEventActive,
  });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const lastRainWidgetFetchRef = useRef<string>('');
  useEffect(() => {
    const latest = metadataTimestamps.forecast_rain;
    if (latest && latest > lastRainWidgetFetchRef.current) {
      lastRainWidgetFetchRef.current = latest;
      setRefreshCounter(c => c + 1);
    }
  }, [metadataTimestamps]);

  const { forecasts, loading } = useRainForecastData(selectedEvent, refreshCounter);

  const tz = selectedEvent?.circuit?.time_zone || 'UTC';

  // Match nowcasting RainForecastCard: sync to player scrubber, else range end
  const selectedDate = useMemo(() => {
    if (playerTimestamp) return playerTimestamp;
    if (selectedDateRange) return selectedDateRange.end;
    return null;
  }, [playerTimestamp, selectedDateRange]);

  const activeForecast = useMemo(
    () => {
      if (!selectedEvent) return null;
      return resolveActiveRainForecast({
        forecasts,
        anchorTime: selectedDate,
        sessions: selectedEvent.sessions ?? [],
        circuitTimezone: tz,
        requireActive: true,
        isRoot,
        currentTime: now,
      });
    },
    [forecasts, selectedDate, selectedEvent, tz, isRoot, now],
  );

  if (loading && forecasts.length === 0) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="small" />
      </div>
    );
  }

  if (!selectedEvent) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize: 11 }}>No event selected</span>} />
      </div>
    );
  }

  if (!selectedDateRange) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span style={{ fontSize: 11 }}>Select a time range on the timeline</span>}
        />
      </div>
    );
  }

  if (!activeForecast) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize: 11 }}>No rain forecast data</span>} />
      </div>
    );
  }

  const isCompact = sizeClass === 'compact';
  const updatedTime = dayjs.utc(activeForecast.value_date).tz(tz);
  const rainStatus = getRainStatus(activeForecast.rain_type, activeForecast.few_drops);
  const statusColor = getStatusColor(rainStatus);
  const statusLabel = getStatusLabel(rainStatus);
  const iconSize = isCompact ? 22 : 28;

  const hasMeta = !isCompact && (
    activeForecast.rain_direction_deg !== null
    || activeForecast.rain_speed_kph !== null
    || activeForecast.rain_start_time
    || activeForecast.rain_end_time
    || activeForecast.rain_pattern
    || activeForecast.rain_intensity !== null
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Status header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: isCompact ? 8 : 10,
          padding: isCompact ? '8px 10px' : '10px 12px',
          background: getCardBackground(rainStatus, isDark),
          borderBottom: `1px solid ${statusColor}40`,
          flexShrink: 0,
        }}
      >
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          {getStatusIcon(rainStatus, statusColor, iconSize)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            strong
            style={{
              fontSize: isCompact ? 13 : 15,
              color: statusColor,
              display: 'block',
              lineHeight: 1.2,
            }}
          >
            {statusLabel}
          </Text>
          <Text
            type="secondary"
            style={{ fontSize: isCompact ? 9 : 10, display: 'block', marginTop: 2 }}
          >
            Updated @ {updatedTime.format('HH:mm')} · {updatedTime.format('ddd DD MMM')}
          </Text>
        </div>
      </div>

      {/* Meta details */}
      {hasMeta && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: '6px 10px',
            borderBottom: '1px solid rgba(128, 128, 128, 0.1)',
            flexShrink: 0,
          }}
        >
          {activeForecast.rain_direction_deg !== null && (
            <Tag
              style={{ margin: 0, fontSize: 10, lineHeight: '18px', padding: '0 6px' }}
              icon={
                <ArrowUpOutlined
                  style={{ fontSize: 10, transform: `rotate(${activeForecast.rain_direction_deg}deg)` }}
                />
              }
            >
              {activeForecast.rain_direction_deg}°
            </Tag>
          )}
          {activeForecast.rain_speed_kph !== null && (
            <Tag style={{ margin: 0, fontSize: 10, lineHeight: '18px', padding: '0 6px' }}>
              {activeForecast.rain_speed_kph} kph
            </Tag>
          )}
          {activeForecast.rain_start_time && (
            <Tag style={{ margin: 0, fontSize: 10, lineHeight: '18px', padding: '0 6px' }}>
              Start: {activeForecast.rain_start_time}
            </Tag>
          )}
          {activeForecast.rain_end_time && (
            <Tag style={{ margin: 0, fontSize: 10, lineHeight: '18px', padding: '0 6px' }}>
              End: {activeForecast.rain_end_time}
            </Tag>
          )}
          {activeForecast.rain_pattern && (
            <Tag style={{ margin: 0, fontSize: 10, lineHeight: '18px', padding: '0 6px' }}>
              {activeForecast.rain_pattern}
            </Tag>
          )}
          {activeForecast.rain_intensity !== null && (
            <Tag style={{ margin: 0, fontSize: 10, lineHeight: '18px', padding: '0 6px' }}>
              Intensity: {activeForecast.rain_intensity}
            </Tag>
          )}
        </div>
      )}

      {/* Forecast message */}
      {activeForecast.message && (
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: isCompact ? '8px 10px' : '10px 12px',
          }}
        >
          <Text
            style={{
              fontSize: isCompact ? 11 : 12,
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              display: 'block',
            }}
          >
            {activeForecast.message}
          </Text>
        </div>
      )}
    </div>
  );
};
